/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import StateService from '../../../../../services/State';
import { buildNC4Pdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { sanitizeAmountInput, sanitizeInteger } from '../utils/numberValidation';
import { BaseStepProps } from './BaseStepProps';
import {
  NC4Draft,
  Schedule2Row,
  buildEmptyNC4,
  PART2_LINES,
  SCH2_SINGLE_BRACKETS,
  SCH2_MFJ_SS_BRACKETS,
  SCH2_HOH_BRACKETS,
  SCH2_MFS_BRACKETS,
} from './nc4/NC4Types';

const NC4_PDF = 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/nc-state-w4.pdf';

const formatSsn = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const radio = (selected: boolean): React.CSSProperties => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? colors.accent : '#9CA3AF'}`,
  position: 'relative',
  flexShrink: 0,
});

const radioInner: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: colors.accent,
};

const filingOptions: { value: NC4Draft['filingStatus']; label: string }[] = [
  { value: 'single_married_separately', label: 'Single or Married Filing Separately' },
  { value: 'head_of_household', label: 'Head of Household' },
  { value: 'married_jointly_surviving_spouse', label: 'Married Filing Jointly or Qualifying Surviving Spouse' },
];

const STATUS_QUESTIONS: {
  prefix: 'single' | 'joint' | 'separate' | 'hoh' | 'surviving';
  label: string;
  questions: { key: string; text: string }[];
}[] = [
  {
    prefix: 'single',
    label: 'For Single filers',
    questions: [
      { key: 'single_itemizedDeductionsExceed', text: 'Will your itemized deductions exceed the standard deduction?' },
      { key: 'single_childDeductionExceed', text: 'Will you claim the child deduction amount?' },
      { key: 'single_federalAdjustments', text: 'Will you have federal adjustments to income?' },
      { key: 'single_claimTaxCredits', text: 'Will you claim NC tax credits?' },
    ],
  },
  {
    prefix: 'joint',
    label: 'For Married Filing Jointly',
    questions: [
      { key: 'joint_itemizedDeductionsExceed', text: 'Will your itemized deductions exceed the standard deduction?' },
      { key: 'joint_childDeductionExceed', text: 'Will you claim the child deduction amount?' },
      { key: 'joint_federalAdjustments', text: 'Will you have federal adjustments to income?' },
      { key: 'joint_claimTaxCredits', text: 'Will you claim NC tax credits?' },
      { key: 'joint_spouseBenefits', text: 'Will your spouse also benefit from withholding allowances?' },
    ],
  },
  {
    prefix: 'separate',
    label: 'For Married Filing Separately',
    questions: [
      {
        key: 'separate_itemizedDeductionsExceed',
        text: 'Will your itemized deductions exceed the standard deduction?',
      },
      { key: 'separate_childDeductionExceed', text: 'Will you claim the child deduction amount?' },
      { key: 'separate_federalAdjustments', text: 'Will you have federal adjustments to income?' },
      { key: 'separate_claimTaxCredits', text: 'Will you claim NC tax credits?' },
    ],
  },
  {
    prefix: 'hoh',
    label: 'For Head of Household',
    questions: [
      { key: 'hoh_itemizedDeductionsExceed', text: 'Will your itemized deductions exceed the standard deduction?' },
      { key: 'hoh_childDeductionExceed', text: 'Will you claim the child deduction amount?' },
      { key: 'hoh_federalAdjustments', text: 'Will you have federal adjustments to income?' },
      { key: 'hoh_claimTaxCredits', text: 'Will you claim NC tax credits?' },
    ],
  },
  {
    prefix: 'surviving',
    label: 'For Qualifying Surviving Spouse',
    questions: [
      {
        key: 'surviving_itemizedDeductionsExceed',
        text: 'Will your itemized deductions exceed the standard deduction?',
      },
      { key: 'surviving_childDeductionExceed', text: 'Will you claim the child deduction amount?' },
      { key: 'surviving_federalAdjustments', text: 'Will you have federal adjustments to income?' },
      { key: 'surviving_claimTaxCredits', text: 'Will you claim NC tax credits?' },
    ],
  },
];

const Schedule2Table = ({
  title,
  brackets,
  data,
  onChange,
}: {
  title: string;
  brackets: { key: keyof NC4Draft; label: string }[];
  data: NC4Draft;
  onChange: (key: keyof NC4Draft, row: Schedule2Row) => void;
}) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{title}</div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: 8,
        fontSize: 12,
        color: colors.muted,
        marginBottom: 4,
      }}
    >
      <div>Income bracket</div>
      <div># Children</div>
      <div>Estimated deduction</div>
    </div>
    {brackets.map(({ key, label }) => {
      const row = data[key] as Schedule2Row;
      return (
        <div key={String(key)} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 13, alignSelf: 'center' }}>{label}</div>
          <input
            type="text"
            inputMode="numeric"
            value={row.numChildren}
            onChange={(e) => onChange(key, { ...row, numChildren: sanitizeInteger(e.target.value) })}
            style={layout.input}
          />
          <input
            type="text"
            inputMode="decimal"
            value={row.estimatedDeduction}
            onChange={(e) => onChange(key, { ...row, estimatedDeduction: sanitizeAmountInput(e.target.value) })}
            placeholder="$0"
            style={layout.input}
          />
        </div>
      );
    })}
  </div>
);

const NC4Step = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const backendDraft = draftDocs.find((d) => d.type === 'NC4');
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);
  const initialFromBackend: NC4Draft = {
    ...buildEmptyNC4(),
    firstName: lookup.firstName,
    lastName: lookup.lastName,
    socialSecurityNumber: lookup.ssn,
    address: lookup.address,
    city: lookup.city,
    state: lookup.state,
    zipCode: lookup.zipCode,
    ...(backendDraft?.options || {}),
    signature: '',
  };
  const initial = (draft as NC4Draft) || initialFromBackend;
  const [data, setData] = useState<NC4Draft>({ ...buildEmptyNC4(), ...initial, signature: '' });
  const [states, setStates] = useState<{ uf: string; name: string }[]>([]);
  const [showPart2, setShowPart2] = useState(false);
  const [showSchedule1, setShowSchedule1] = useState(false);
  const [showSchedule2, setShowSchedule2] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    StateService('')
      .listSelect()
      .then((result: any) => setStates(result?.data || []))
      .catch(() => setStates([]));
  }, []);

  const update = (patch: Partial<NC4Draft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const updateSchedule2Row = (key: keyof NC4Draft, row: Schedule2Row) => {
    update({ [key]: row } as Partial<NC4Draft>);
  };

  const canOpenSignature =
    !!data.firstName &&
    !!data.lastName &&
    !!data.socialSecurityNumber &&
    !!data.address &&
    !!data.city &&
    !!data.state &&
    !!data.zipCode &&
    !!data.filingStatus &&
    !!data.totalAllowances &&
    !savingDraft;

  const livePdfUrl = useLivePdfPreview({
    builder: async () =>
      (await buildNC4Pdf(NC4_PDF, { ...data, employeeSignature: '', signatureDate: '' }, '')) || undefined,
    deps: [data],
  });

  const submitWithSignature = async (signaturePng: string) => {
    const formData = {
      ...data,
      employeeSignature: signaturePng,
      signatureDate: moment().format('MM/DD/YYYY'),
    };
    const pdfBytes = await buildNC4Pdf(NC4_PDF, formData, signaturePng);
    const pdfSigned = pdfBytes ? await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' })) : null;

    await HireDocuments('').create({
      type: 'NC4',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl: NC4_PDF,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        ...formData,
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('NC-4 signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const formData = {
        ...data,
        employeeSignature: '',
        signatureDate: '',
      };
      await HireDocuments('').create({
        type: 'NC4',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl: NC4_PDF,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: { ...formData, signPng: '' },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save NC-4.');
    } finally {
      setSavingDraft(false);
    }
  };

  const yesNoRow = (key: string, text: string) => {
    const value = data[key];
    return (
      <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, flex: 1 }}>{text}</span>
        {(['yes', 'no'] as const).map((opt) => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="radio"
              name={key}
              checked={value === opt}
              onChange={() => update({ [key]: opt } as Partial<NC4Draft>)}
            />
            {opt === 'yes' ? 'Yes' : 'No'}
          </label>
        ))}
      </div>
    );
  };

  const collapseBtn = (open: boolean, label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: colors.accent,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0,
        marginTop: 14,
        marginBottom: 8,
        display: 'block',
      }}
    >
      {open ? '−' : '+'} {label}
    </button>
  );

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
        Form NC-4: Employee&apos;s Withholding Allowance Certificate (NC)
      </h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Review the form below, complete the fields and sign at the bottom.
      </p>

      <InlinePdfViewer url={livePdfUrl || NC4_PDF} maxHeight="55vh" />

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Personal Information</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.5fr 2fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>First Name *</label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>M.I.</label>
          <input
            type="text"
            maxLength={1}
            value={data.middleInitial}
            onChange={(e) => update({ middleInitial: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Last Name *</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Social Security Number *</label>
        <input
          type="text"
          value={data.socialSecurityNumber}
          onChange={(e) => update({ socialSecurityNumber: formatSsn(e.target.value) })}
          placeholder="XXX-XX-XXXX"
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Street Address *</label>
        <input
          type="text"
          value={data.address}
          onChange={(e) => update({ address: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>City *</label>
          <input
            type="text"
            value={data.city}
            onChange={(e) => update({ city: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>State *</label>
          <select value={data.state} onChange={(e) => update({ state: e.target.value })} style={layout.select}>
            <option value="">Select...</option>
            {states.map((s) => (
              <option key={s.uf} value={s.uf}>
                {s.uf}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={layout.label}>ZIP code *</label>
          <input
            type="text"
            value={data.zipCode}
            onChange={(e) => update({ zipCode: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Country</label>
        <input
          type="text"
          value={data.country}
          onChange={(e) => update({ country: e.target.value })}
          style={layout.input}
        />
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Filing Status</h4>
      <div style={{ marginBottom: 16 }}>
        {filingOptions.map((opt) => (
          <div
            key={opt.value}
            onClick={() => update({ filingStatus: opt.value })}
            style={{
              ...layout.optionCard,
              ...(data.filingStatus === opt.value ? layout.optionCardSelected : {}),
            }}
          >
            <div style={radio(data.filingStatus === opt.value)}>
              {data.filingStatus === opt.value && <div style={radioInner} />}
            </div>
            {opt.label}
          </div>
        ))}
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Allowances</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Total number of allowances you are claiming *</label>
          <input
            type="text"
            inputMode="numeric"
            value={data.totalAllowances}
            onChange={(e) => update({ totalAllowances: sanitizeInteger(e.target.value) })}
            placeholder="0"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Additional amount to withhold per pay period</label>
          <input
            type="text"
            inputMode="decimal"
            value={data.additionalAmount}
            onChange={(e) => update({ additionalAmount: sanitizeAmountInput(e.target.value) })}
            placeholder="$0"
            style={layout.input}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Allowance Worksheet — Yes/No Questions</h4>
      <p style={{ color: colors.muted, fontSize: 12, marginTop: 0 }}>
        Answer the questions for your filing status only. Answers in other sections may be left blank.
      </p>
      {STATUS_QUESTIONS.map((group) => (
        <div key={group.prefix} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{group.label}</div>
          {group.questions.map((q) => yesNoRow(q.key, q.text))}
        </div>
      ))}

      {collapseBtn(showPart2, 'Part II — Allowances Worksheet (Lines 1–17)', () => setShowPart2(!showPart2))}
      {showPart2 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {PART2_LINES.map(({ key, label }) => (
            <div key={String(key)}>
              <label style={layout.label}>{label}</label>
              <input
                type="text"
                inputMode="decimal"
                value={data[key] as string}
                onChange={(e) => update({ [key]: sanitizeAmountInput(e.target.value) } as Partial<NC4Draft>)}
                placeholder="$0"
                style={layout.input}
              />
            </div>
          ))}
        </div>
      )}

      {collapseBtn(showSchedule1, 'Schedule 1 — Itemized Deductions', () => setShowSchedule1(!showSchedule1))}
      {showSchedule1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {(
            [
              ['sch1_homeMortgageInterest', 'Home mortgage interest'],
              ['sch1_realEstatePropertyTaxes', 'Real estate property taxes'],
              ['sch1_charitableContributions', 'Charitable contributions'],
              ['sch1_medicalAndDentalExpenses', 'Medical and dental expenses'],
              ['sch1_repaymentOfClaimOfRightIncome', 'Repayment of claim of right income'],
              ['sch1_totalEstimatedItemizedDeductions', 'Total estimated itemized deductions'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label style={layout.label}>{label}</label>
              <input
                type="text"
                inputMode="decimal"
                value={data[key] as string}
                onChange={(e) => update({ [key]: sanitizeAmountInput(e.target.value) } as Partial<NC4Draft>)}
                placeholder="$0"
                style={layout.input}
              />
            </div>
          ))}
        </div>
      )}

      {collapseBtn(showSchedule2, 'Schedule 2 — Child Deduction Amount', () => setShowSchedule2(!showSchedule2))}
      {showSchedule2 && (
        <>
          <Schedule2Table title="Single" brackets={SCH2_SINGLE_BRACKETS} data={data} onChange={updateSchedule2Row} />
          <Schedule2Table
            title="Married Filing Jointly / Qualifying Surviving Spouse"
            brackets={SCH2_MFJ_SS_BRACKETS}
            data={data}
            onChange={updateSchedule2Row}
          />
          <Schedule2Table
            title="Head of Household"
            brackets={SCH2_HOH_BRACKETS}
            data={data}
            onChange={updateSchedule2Row}
          />
          <Schedule2Table
            title="Married Filing Separately"
            brackets={SCH2_MFS_BRACKETS}
            data={data}
            onChange={updateSchedule2Row}
          />
          <div style={{ marginBottom: 16 }}>
            <label style={layout.label}>Total estimated child deduction</label>
            <input
              type="text"
              inputMode="decimal"
              value={data.sch2_totalEstimatedChildDeduction}
              onChange={(e) => update({ sch2_totalEstimatedChildDeduction: sanitizeAmountInput(e.target.value) })}
              placeholder="$0"
              style={layout.input}
            />
          </div>
        </>
      )}

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign NC-4"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default NC4Step;
